import { CrossCircledIcon } from '@radix-ui/react-icons';
import { Button, Flex } from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { SessionEventsContext } from 'contexts/session-events.context';
import {
  DETECTION_FAILED_DELAY_S,
  MAX_CONSECUTIVE_FAILS,
  TrainingContext,
} from 'contexts/training.context';
import { MachineSettingsTabKey, SettingsSection } from 'enums/settings';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useEffect, useRef, useState } from 'react';

export const PTDetectionFailureCallout = (props: { testing?: boolean }) => {
  const { failures, warnFailure, dismissFailedCallout } =
    useContext(TrainingContext);

  const { setDialogSettings } = useContext(SessionEventsContext);

  const atMaxFailures = failures >= MAX_CONSECUTIVE_FAILS;

  const [count, setCount] = useState(
    atMaxFailures ? -1 : DETECTION_FAILED_DELAY_S
  );
  const cdInterval = useRef<NodeJS.Timer>();

  // default countdown behaviour
  useEffect(() => {
    if (atMaxFailures) {
      // force user to interact with the callout
      return;
    }

    cdInterval.current = setInterval(
      () => {
        setCount((prev) => prev - 1);
      },
      // count down every 10s instead of 1s while testing
      props.testing ? 10_000 : 1_000
    );

    return () => clearInterval(cdInterval.current);
  }, [props.testing, atMaxFailures]);

  // automatically cancel the countdown interval if count is ended
  // e.g. from mouse over
  useEffect(() => {
    switch (count) {
      case -1: {
        // cancelled from opening actions menu
        clearInterval(cdInterval.current);
        return;
      }

      case 0: {
        // countdown reached 0
        clearInterval(cdInterval.current);

        // default action on timeout
        dismissFailedCallout(atMaxFailures);
        return;
      }

      default: {
        // do nothing
        return;
      }
    }
  }, [count, atMaxFailures]);

  const shouldWarn = props.testing || warnFailure || failures !== 0;

  if (!shouldWarn) {
    return null;
  }

  return (
    <ErrorBoundary identifier="PTDetectionFailureCallout">
      <CommonCallout
        icon={atMaxFailures ? <CrossCircledIcon /> : undefined}
        color={atMaxFailures ? RADIX.COLOR.DANGER : undefined}
        flexAlign="start"
        content={
          <Flex
            direction="column"
            onMouseOver={
              // stop the countdown
              () => setCount(-1)
            }
            onTouchMove={
              // stop the countdown
              () => setCount(-1)
            }
          >
            <p>
              {atMaxFailures
                ? t('tr.max-no-pitch-detected-msg')
                : t('tr.no-pitch-detected-msg')}
            </p>

            {count !== -1 && !atMaxFailures && (
              <p>
                Will resume firing in{' '}
                {count === 1 ? `${count} second` : `${count} seconds`}...
              </p>
            )}

            <Flex mt="3" gap="2" justify="end">
              <Button
                variant="soft"
                className="width-120px"
                color={RADIX.COLOR.NEUTRAL}
                onClick={
                  // reset the failure count
                  () => dismissFailedCallout(atMaxFailures)
                }
              >
                {atMaxFailures ? t('tr.resume-training') : t('common.dismiss')}
              </Button>

              <Button
                className="width-120px"
                color={RADIX.COLOR.ACCENT}
                onClick={() =>
                  setDialogSettings({
                    identifier: 'DetectionFailedSettingsDialog',
                    initialSection: SettingsSection.Machine,
                    sections: [SettingsSection.Machine],
                    initialMachineTab: MachineSettingsTabKey.TrackingDevice,
                    onClose: () => setDialogSettings(undefined),
                  })
                }
              >
                {t('common.open-settings')}
              </Button>
            </Flex>
          </Flex>
        }
      />
    </ErrorBoundary>
  );
};
